import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import '../styles/PageBanner.css';

/**
 * REUSABLE PAGE BANNER COMPONENT
 * Features: Animated particles, parallax effect, gradient background
 * Use on all pages except homepage
 */

const PageBanner = ({ title, subtitle, backgroundGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }) => {
  const [particles, setParticles] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Generate random particles on mount
  useEffect(() => {
    const particleArray = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.5 + 0.2,
    }));
    setParticles(particleArray);
  }, []);

  // Track mouse position for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Calculate parallax offset
  const parallaxOffset = {
    x: (mousePosition.x - window.innerWidth / 2) * 0.02,
    y: (mousePosition.y - window.innerHeight / 2) * 0.02,
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="page-banner-wrapper">
      <div
        className="page-banner"
        style={{ background: backgroundGradient }}
      >
        {/* Animated Particles */}
        <div className="particles-container">
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className="particle"
              style={{
                left: `${particle.left}%`,
                top: `${particle.top}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                opacity: particle.opacity,
              }}
              animate={{
                y: [0, -20, 0],
                x: [0, Math.random() * 20 - 10, 0],
              }}
              transition={{
                duration: particle.duration,
                delay: particle.delay,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>

        {/* Parallax Background Shape */}
        <motion.div
          className="parallax-shape"
          style={{
            x: parallaxOffset.x,
            y: parallaxOffset.y,
          }}
        />

        {/* Content */}
        <div className="banner-content">
          <motion.h1
            className="banner-title"
            variants={titleVariants}
            initial="hidden"
            animate="visible"
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              className="banner-subtitle"
              variants={subtitleVariants}
              initial="hidden"
              animate="visible"
            >
              {subtitle}
            </motion.p>
          )}

          {/* Scroll Indicator */}
          <motion.div
            className="scroll-indicator"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="scroll-text">Scroll to explore</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 5v14M5 12l7 7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PageBanner;
